/*eslint complexity: ["error", 23]*/
/*eslint max-lines-per-function: ["error", 300]*/
import { DefaultApi } from '../autogenerated/spdApiClient/apis/DefaultApi';
import { GetCardDto, GetLastEditorDto } from '../autogenerated/spdApiClient';
import {
  CreateUpdateCardDto,
  CreateUpdateCardDtoFromJSON,
} from '../autogenerated/spdApiClient/models/CreateUpdateCardDto';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import { cardForGrid } from '../../../spd/components/Cards/CardsSearch';
import { CardSchema, CardType, TemplateType } from '../validation/schemas';
import { validateAgainst } from '../validation/validateAgainst';

export type CardEntity = GetCardDto;

export interface CardContextInterface {
  get: (id: string) => Promise<CardEntity>;
  getCardByExam: (id: string, type: CardType) => Promise<CardEntity>;
  getAllByExam: (id: string) => Promise<CardEntity[]>;
  getAllByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllCount: (filterText?: string, filterColumns?: string) => Promise<number>;
  create: (dto: CreateUpdateCardDto) => Promise<GetCardDto>;
  update: (id: string, dto: CreateUpdateCardDto) => Promise<void>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;

  cardData: (
    examId: string,
    type: TemplateType,
    card?: GetCardDto,
    cardDate?: Date | null,
  ) => Promise<CreateUpdateCardDto>;
  getForSigningByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    userUid?: string,
  ) => Promise<GridRecord[]>;
  getForSigningCount: (
    filterText?: string,
    userUid?: string,
  ) => Promise<number>;
  getAllForUserToSign: (userUid?: string) => Promise<GetCardDto[]>;
  getAllAutomaticByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllAutomaticCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
}

export const CardContext = (api: DefaultApi) => {
  return {
    get: async (id: string) => {
      return await api.cardControllerGet(id);
    },

    getCardByExam: async (id: string, type: CardType) => {
      return await api.cardControllerGetByExam(id, type);
    },

    getAllByExam: async (id: string) => {
      return await api.cardControllerGetAllByExam(id);
    },

    getAllByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.cardControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );
      return data.map(cardForGrid);
    },

    getAllCount: (filterText?: string, filterColumns?: string) =>
      api.cardControllerGetAllCount(filterText || '', filterColumns),

    create: (dto: CreateUpdateCardDto) => api.cardControllerCreate(dto),

    update: (id: string, dto: CreateUpdateCardDto) =>
      api.cardControllerUpdate(id, dto),

    getLastEditor: (id: string) => api.cardControllerGetLastEditor(id),

    cardData: async (
      examId: string,
      type: TemplateType,
      card?: GetCardDto,
      cardData?: Date | null,
    ) => {
      if (card) {
        return CreateUpdateCardDtoFromJSON({
          ...card,
          ...{ psychologistUID: card.psychologist?.uid || '' },
        });
      }
      if (type === TemplateType.Psychological_Exam_card) {
        return CreateUpdateCardDtoFromJSON({
          pdfTemplateType: type,
          pass: true,
          cardDate: cardData || new Date(),
          interview: await api.answerControllerGetInterviewText(examId),
          intellectualAbility:
            await api.answerControllerGetIntellectualAbilityText(examId),
          personality: await api.answerControllerGetPersonalityText(examId),
          psychomotorEfficiency:
            await api.answerControllerGetIntellectualPsychomotorEfficiencyText(
              examId,
            ),
          justification: `W standardowo wykonanym badaniu nie stwierdzono wyników poniżej normy. Brak psychologicznych przeciwwskazań do wykonywania pracy na stanowisku kierowcy.`,
          conclusions: `Interpretacji wyników dokonano uwzględniając przedziały ufności i standardowy błąd pomiaru. Nie uzyskano wyników będących podstawą do orzeczenia o istnieniu przeciwwskazań psychologicznych.`,
          recommendations: `Wszystkie uzyskane wyniki w badaniu psychologicznym znajdują się w normie, lub są powyżej normy. Brak dodatkowych zaleceń.`,
          type: CardType.Psychologist,
          externalAppearance: 'typowy',
          behaviorTesting: 'adekwatne do sytuacji',
          generalMood: 'zrównoważony',
          levelOfInterest: 'zainteresowany',
          attitudeToPsychologist: 'łatwy w kontakcie',
          abilityToExpressYourself: 'dobra',
          selfControl: 'lęk nie wpływa na wyniki',
          reactionToFailure: 'próbuje unikać',
          understandingTheInstructions: 'przeciętnie',
          attention: 'dość uważny',
          workPace: 'średnie',
          arousalLevel: 'aktywny',
          behaviorUnderPressure: 'skoncentrowany',
        });
      }
      if (type === TemplateType.Weapon_psychological_exam_card) {
        return CreateUpdateCardDtoFromJSON({
          pdfTemplateType: type,
          pass: true,
          cardDate: cardData || new Date(),
          interview: await api.answerControllerGetInterviewText(examId),
          intellectualAbility:
            await api.answerControllerGetIntellectualAbilityText(examId),
          personality: await api.answerControllerGetPersonalityText(examId),
          psychomotorEfficiency:
            await api.answerControllerGetIntellectualPsychomotorEfficiencyText(
              examId,
            ),
          justification: `W standardowo wykonanym badaniu nie stwierdzono wyników poniżej normy. Brak psychologicznych przeciwwskazań do dysponowania bronią.`,
          conclusions: `Interpretacji wyników dokonano uwzględniając przedziały ufności i standardowy błąd pomiaru. Nie uzyskano wyników będących podstawą do orzeczenia o istnieniu przeciwwskazań psychologicznych.`,
          recommendations: `Wszystkie uzyskane wyniki w badaniu psychologicznym znajdują się w normie, lub są powyżej normy. Brak dodatkowych zaleceń.`,
          type: CardType.PsychologistWeapon,
          externalAppearance: 'typowy',
          behaviorTesting: 'adekwatne do sytuacji',
          generalMood: 'zrównoważony',
          levelOfInterest: 'zainteresowany',
          attitudeToPsychologist: 'łatwy w kontakcie',
          abilityToExpressYourself: 'dobra',
          selfControl: 'lęk nie wpływa na wyniki',
          reactionToFailure: 'próbuje unikać',
          understandingTheInstructions: 'przeciętnie',
          attention: 'dość uważny',
          workPace: 'średnie',
          arousalLevel: 'aktywny',
          behaviorUnderPressure: 'skoncentrowany',
        });
      }

      if (type === TemplateType.Preventive_Exam_card) {
        const cardDate = cardData || new Date();
        return CreateUpdateCardDtoFromJSON({
          type: CardType.Preventive,
          pass: true,
          pdfTemplateType: type,
          cardDate: cardDate,
          physicalSkin: 'Norma',
          physicalSkull: 'Norma',
          physicalLymphNodes: 'Norma',
          physicalNose: 'Norma',
          physicalOropharyngealCavity: 'Norma',
          physicalNeck: 'Norma',
          physicalChest: 'Norma',
          physicalLungs: 'Norma',
          physicalCardiovascularSystem: 'Norma',
          physicalAbdominalCavity: 'Norma',
          physicalGenitourinarySystem: 'Norma',
          physicalLocomotorSystem: 'Norma',
          physicalNervousSystem: 'Norma',
          physicalMentalState: 'Norma',
          recommendations: 'Brak',
          justification: 'Stan ogólny dobry, brak przeciwwskazań do pracy',
        });
      }

      if (type === TemplateType.Weapon_card) {
        return CreateUpdateCardDtoFromJSON({
          type: CardType.Weapon,
          pass: true,
          pdfTemplateType: type,
          cardDate: cardData || new Date(),
          physicalSkin: 'Nie badano',
          physicalSkull: 'Nie badano',
          physicalLymphNodes: 'Nie badano',
          physicalNose: 'Nie badano',
          physicalOropharyngealCavity: 'Nie badano',
          physicalNeck: 'Nie badano',
          physicalChest: 'Nie badano',
          physicalLungs: 'Nie badano',
          physicalCardiovascularSystem: 'Nie badano',
          physicalAbdominalCavity: 'Nie badano',
          physicalGenitourinarySystem: 'Nie badano',
          physicalLocomotorSystem: 'Nie badano',
          physicalNervousSystem: 'Nie badano',
          physicalMentalState: 'Nie badano',
        });
      }
      if (type === TemplateType.Sanitary_Exam_card) {
        return CreateUpdateCardDtoFromJSON({
          type: CardType.Sanitary,
          pass: true,
          pdfTemplateType: type,
          cardDate: cardData || new Date(),
          physicalSkin: 'Nie badano',
          physicalSkull: 'Nie badano',
          physicalLymphNodes: 'Nie badano',
          physicalNose: 'Nie badano',
          physicalOropharyngealCavity: 'Nie badano',
          physicalNeck: 'Nie badano',
          physicalChest: 'Nie badano',
          physicalLungs: 'Nie badano',
          physicalCardiovascularSystem: 'Nie badano',
          physicalAbdominalCavity: 'Nie badano',
          physicalGenitourinarySystem: 'Nie badano',
          physicalLocomotorSystem: 'Nie badano',
          physicalNervousSystem: 'Nie badano',
          physicalMentalState: 'Nie badano',
        });
      }
      if (type === TemplateType.Sport_card) {
        return CreateUpdateCardDtoFromJSON({
          type: CardType.Sport,
          pass: true,
          pdfTemplateType: type,
          cardDate: cardData || new Date(),
        });
      }

      if (type === TemplateType.Driver_Exam_card) {
        return CreateUpdateCardDtoFromJSON({
          type: CardType.Driver,
          pass: true,
          pdfTemplateType: type,
          cardDate: cardData || new Date(),
          physicalSkin: 'Norma',
          physicalSkull: 'Norma',
          physicalLymphNodes: 'Norma',
          physicalNose: 'Norma',
          physicalOropharyngealCavity: 'Nie badano',
          physicalNeck: 'Norma',
          physicalChest: 'Norma',
          physicalLungs: 'Nie badano',
          physicalCardiovascularSystem: 'Nie badano',
          physicalAbdominalCavity: 'Nie badano',
          physicalGenitourinarySystem: 'Nie badano',
          physicalLocomotorSystem: 'Norma',
          physicalNervousSystem: 'Norma',
          physicalMentalState: 'Norma',
          justification:
            'Stan ogólny dobry, brak przeciwwskazań do kierowania pojazdami',
          conclusions: 'Brak',
        });
      }

      return CreateUpdateCardDtoFromJSON({
        type: CardType.Driver,
        pass: true,
        pdfTemplateType: type,
        cardDate: cardData || new Date(),
        physicalSkin: 'Norma',
        physicalSkull: 'Norma',
        physicalLymphNodes: 'Norma',
        physicalNose: 'Norma',
        physicalOropharyngealCavity: 'Nie badano',
        physicalNeck: 'Norma',
        physicalChest: 'Norma',
        physicalLungs: 'Nie badano',
        physicalCardiovascularSystem: 'Nie badano',
        physicalAbdominalCavity: 'Nie badano',
        physicalGenitourinarySystem: 'Nie badano',
        physicalLocomotorSystem: 'Norma',
        physicalNervousSystem: 'Norma',
        physicalMentalState: 'Norma',
      });
    },

    getForSigningByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      userUid?: string,
    ) => {
      const data = await api.cardControllerGetForSigningByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        userUid,
      );
      return data.map(card => cardForGrid(card));
    },

    getForSigningCount: (filterText?: string, userUid?: string) =>
      api.cardControllerGetForSigningCount(filterText || '', userUid),

    getAllForUserToSign: (userUid?: string) =>
      api.cardControllerGetAllForUserToSign(userUid),

    getAllAutomaticByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.cardControllerGetAllAutomaticByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );
      return data.map(cardForGrid);
    },

    getAllAutomaticCount: (filterText?: string, filterColumns?: string) =>
      api.cardControllerGetAllAutomaticCount(filterText || '', filterColumns),
  };
};

export const validate = (card: CreateUpdateCardDto) => {
  return validateAgainst(CardSchema, card);
};
